import {
  decodeVisualGroups,
  type VisualGrouping,
  type RawVisualGrouping,
} from "../visualGroups";
import { defaultThemeV2 } from "../../theme/defaultThemeV2";
import type { QuestionCore } from "../contracts";
import { encodeResponses } from "../responseEncoding";

export type GridThisOrThatRatingRaw = RawVisualGrouping & {
  type: "grid_this_or_that_rating";
  content: string;
  style: "button" | "slider";
  rows?: { value: string }[];
  row_options: { value: number; text: string; display_text: boolean }[][];
  dont_know_option: { value: string; text: string } | null;
  previous_response?: (string | number)[];
  image?: string | null;
  alt_text?: string | null;
  error_message?: string;
};

type Point = { value: number; label: string; displayLabel: boolean };

export type GridThisOrThatRatingDefinition = VisualGrouping & {
  rowIds?: readonly string[];
  prompt: string;
  rows: readonly (readonly Point[])[];
  dontKnow?: { value: string; label: string };
  previousResponses: readonly (string | number)[];
  image?: string;
  altText?: string;
  error?: string;
};

export type GridThisOrThatRatingDraft = {
  selectedByRow: readonly (number | string | null)[];
};

export type GridThisOrThatRatingEvent =
  | { type: "select"; rowIndex: number; value: number }
  | { type: "dont-know"; rowIndex: number };

export type GridThisOrThatRatingResponse = {
  type: "grid_this_or_that_rating";
  value: string;
  responses: string;
};

const validValue = (
  definition: GridThisOrThatRatingDefinition,
  rowIndex: number,
  value: number | string | null | undefined,
) => {
  if (value === definition.dontKnow?.value) return value;
  return definition.rows[rowIndex]?.find((point) => point.value === value)
    ?.value;
};

export const gridThisOrThatRatingCore: QuestionCore<
  GridThisOrThatRatingRaw,
  GridThisOrThatRatingDefinition,
  GridThisOrThatRatingDraft,
  GridThisOrThatRatingEvent,
  GridThisOrThatRatingResponse
> = {
  decode: (raw) => {
    if (
      !Array.isArray(raw.row_options) ||
      raw.row_options.length === 0 ||
      raw.row_options.some((row) => !Array.isArray(row) || row.length < 2)
    ) {
      throw new Error(
        "This-or-that rating requires scales with at least two points.",
      );
    }
    if (
      raw.row_options.some((row) => {
        const values = row.map((point) => point.value);
        return (
          values.some((value) => !Number.isInteger(value)) ||
          new Set(values).size !== values.length
        );
      })
    ) {
      throw new Error(
        "This-or-that rating points must have unique integer values.",
      );
    }
    if (
      raw.previous_response !== undefined &&
      (!Array.isArray(raw.previous_response) ||
        raw.previous_response.some(
          (value) => typeof value !== "string" && typeof value !== "number",
        ))
    ) {
      throw new Error(
        "This-or-that rating previous response must be a list of values.",
      );
    }
    return {
      ...decodeVisualGroups(raw),
      ...(raw.rows ? { rowIds: raw.rows.map((row) => row.value) } : {}),
      prompt: raw.content,
      rows: raw.row_options.map((row) =>
        row.map((point) => ({
          value: point.value,
          label: point.text,
          displayLabel: point.display_text,
        })),
      ),
      previousResponses: raw.previous_response ?? [],
      ...(raw.dont_know_option
        ? {
            dontKnow: {
              value: raw.dont_know_option.value,
              label: raw.dont_know_option.text,
            },
          }
        : {}),
      ...(raw.image ? { image: raw.image } : {}),
      ...(raw.alt_text ? { altText: raw.alt_text } : {}),
      ...(raw.error_message ? { error: raw.error_message } : {}),
    };
  },
  initialize: (definition) => ({
    selectedByRow: definition.rows.map((row, index) => {
      const previous = definition.previousResponses[index];
      if (previous === definition.dontKnow?.value) return previous;
      return (
        row.find((point) => String(point.value) === String(previous))?.value ??
        null
      );
    }),
  }),
  update: (definition, draft, event) => {
    if (!Number.isInteger(event.rowIndex) || !definition.rows[event.rowIndex])
      return draft;
    const value =
      event.type === "dont-know"
        ? definition.dontKnow?.value
        : definition.rows[event.rowIndex].find(
            (point) => point.value === event.value,
          )?.value;
    if (value === undefined) return draft;
    const selectedByRow = [...draft.selectedByRow];
    selectedByRow[event.rowIndex] = value;
    return { selectedByRow };
  },
  validate: (definition, draft, copy = defaultThemeV2.copy) =>
    definition.rows.every(
      (_, index) =>
        validValue(definition, index, draft.selectedByRow[index]) !== undefined,
    )
      ? { valid: true }
      : { valid: false, error: copy.validation.rateEachRow },
  encode: (definition, draft) => {
    const validation = gridThisOrThatRatingCore.validate(definition, draft);
    if (!validation.valid) throw new Error(validation.error);
    const values = definition.rows.map(
      (_, index) => validValue(definition, index, draft.selectedByRow[index])!,
    );
    return {
      type: "grid_this_or_that_rating",
      value: values.join(","),
      responses: encodeResponses(values),
    };
  },
  projectTranscript: (definition, draft) => {
    const validation = gridThisOrThatRatingCore.validate(definition, draft);
    if (!validation.valid) throw new Error(validation.error);
    return {
      kind: "grid_this_or_that_rating",
      question: definition.prompt,
      answer: definition.rows
        .map((row, index) => {
          const value = validValue(
            definition,
            index,
            draft.selectedByRow[index],
          )!;
          const point = row.find((item) => item.value === value);
          const endpoints = `${row[0].label} to ${row[row.length - 1].label}`;
          const answer = point
            ? String(point.value)
            : definition.dontKnow!.label;
          return `${endpoints}: ${answer}`;
        })
        .join("\n"),
    };
  },
};
